import React from 'react';
import { Container, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import {
  CheckIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';
import { SubscriptionPlan } from '../types';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { usePricing } from './hooks/usePricing';

const Pricing: React.FC = () => {
  const {
    highlights, loading, checkoutLoading, checkoutError, setCheckoutError, loggedInUser, currentPlanSlug,
    freePlan, paidPlans, isFree, getPlanColor, getLimitLabel,
    formatPrice, handlePlanClick, navigateToRegister,
  } = usePricing();

  const renderPlanCard = (plan: SubscriptionPlan, isWide = false) => {
    const colors = getPlanColor(plan.slug);
    const isFeatured = plan.is_featured;
    const isCurrentPlan = currentPlanSlug === plan.slug;

    return (
      <div
        className="pricing-plan-card"
        style={{
          borderRadius: '16px',
          border: isFeatured ? `2px solid ${colors.accent}` : isCurrentPlan ? '2px solid #059669' : '1px solid rgba(226,232,240,0.6)',
          boxShadow: isFeatured
            ? '0 24px 48px rgba(0,0,0,0.2)'
            : isCurrentPlan ? '0 12px 36px rgba(5,150,105,0.15)' : '0 8px 32px rgba(0,0,0,0.12)',
          position: 'relative',
          overflow: 'visible',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          background: '#fff',
          display: 'flex',
          flexDirection: isWide ? 'row' as const : 'column' as const,
          width: '100%',
          height: '100%',
        }}
      >
        {/* Badge */}
        {(plan.badge_text || isCurrentPlan) && (
          <div style={{
            position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 10,
          }}>
            <span
              style={{
                background: isCurrentPlan ? '#059669' : colors.btn,
                color: '#fff', padding: '6px 16px', borderRadius: '20px',
                fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                boxShadow: isCurrentPlan ? '0 4px 14px rgba(5,150,105,0.4)' : `0 4px 14px ${colors.accent}40`,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                letterSpacing: '0.03em', textTransform: 'uppercase',
              }}
            >
              {isCurrentPlan ? (
                <CheckIcon style={{ width: '11px', height: '11px' }} />
              ) : (
                <SparklesIcon style={{ width: '11px', height: '11px' }} />
              )}
              {isCurrentPlan ? 'Current Plan' : plan.badge_text}
            </span>
          </div>
        )}

        {isWide ? (
          /* ── Wide layout for Free Trial banner ── */
          <div style={{
            padding: '2rem 2.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '3rem',
            flexWrap: 'wrap',
            width: '100%',
          }}>
            {/* Left: trial headline */}
            <div style={{ minWidth: '200px' }}>
              <h5 style={{ fontWeight: 800, color: colors.accent, marginBottom: '0.25rem', fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
                {plan.trial_days > 0 ? `${plan.trial_days}-Day Free Trial` : plan.name}
              </h5>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0, fontWeight: 500 }}>
                No credit card required
              </p>
            </div>

            {/* Center: features */}
            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '6px 32px', minWidth: '200px' }}>
              {plan.features.map((feature: string) => (
                <div key={feature} className="d-flex align-items-center gap-2" style={{ minWidth: '180px' }}>
                  <CheckIcon style={{ width: '15px', height: '15px', color: colors.accent, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.84rem', color: '#4b5563', lineHeight: 1.4 }}>{feature}</span>
                </div>
              ))}
            </div>

            {/* Right: CTA */}
            <div
              onClick={() => !isCurrentPlan && handlePlanClick(plan)}
              onKeyDown={e => !isCurrentPlan && (e.key === 'Enter' || e.key === ' ') && handlePlanClick(plan)}
              className="d-flex align-items-center justify-content-center gap-2"
              role="button"
              tabIndex={0}
              style={{
                borderRadius: '10px', padding: '12px 32px', fontWeight: 700, fontSize: '0.9rem',
                background: isCurrentPlan ? '#059669' : colors.btn, color: '#fff', border: 'none',
                boxShadow: isCurrentPlan ? '0 4px 14px rgba(5,150,105,0.25)' : `0 4px 14px ${colors.accent}25`,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                cursor: isCurrentPlan ? 'default' : 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                opacity: checkoutLoading === plan.id ? 0.7 : 1,
                pointerEvents: checkoutLoading === plan.id ? 'none' as const : 'auto' as const,
              }}
              onMouseEnter={e => { if (!isCurrentPlan) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${colors.accent}35`; } }}
              onMouseLeave={e => { if (!isCurrentPlan) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 14px ${colors.accent}25`; } }}
            >
              {checkoutLoading === plan.id ? (
                <Spinner animation="border" size="sm" />
              ) : isCurrentPlan ? (
                <CheckIcon style={{ width: '16px', height: '16px' }} />
              ) : (
                <RocketLaunchIcon style={{ width: '16px', height: '16px' }} />
              )}
              {isCurrentPlan ? 'Current Plan' : (plan.cta_text || 'Start Free Trial')}
            </div>
          </div>
        ) : (
          /* ── Vertical layout for paid cards ── */
          <div style={{
            padding: '1.75rem 1.5rem 1.5rem',
            paddingTop: (plan.badge_text || isCurrentPlan) ? '2.25rem' : '1.75rem',
            display: 'flex', flexDirection: 'column', flex: 1,
          }}>
            <h5 style={{ fontWeight: 800, color: colors.accent, marginBottom: '0.5rem', fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
              {plan.name}
            </h5>

            <div style={{ marginBottom: '1rem' }}>
              <div className="d-flex align-items-baseline">
                <span style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.03em' }}>
                  {formatPrice(plan.price)}
                </span>
                <span style={{ fontSize: '0.95rem', color: '#94a3b8', fontWeight: 500, marginLeft: '2px' }}>/mo</span>
              </div>
            </div>

            <div style={{
              background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: '10px',
              padding: '10px 14px', marginBottom: '1.25rem', fontSize: '0.82rem',
              fontWeight: 700, color: colors.text, textAlign: 'center',
            }}>
              {getLimitLabel(plan)}
            </div>

            <div style={{ flex: 1, marginBottom: '1.5rem' }}>
              {plan.features.map((feature: string) => (
                <div key={feature} className="d-flex align-items-start gap-2" style={{ marginBottom: '10px' }}>
                  <CheckIcon style={{ width: '15px', height: '15px', color: colors.accent, flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontSize: '0.84rem', color: '#4b5563', lineHeight: 1.4 }}>{feature}</span>
                </div>
              ))}
            </div>

            <div
              onClick={() => !isCurrentPlan && handlePlanClick(plan)}
              onKeyDown={e => !isCurrentPlan && (e.key === 'Enter' || e.key === ' ') && handlePlanClick(plan)}
              className="d-flex align-items-center justify-content-center gap-2"
              role="button"
              tabIndex={0}
              style={{
                borderRadius: '10px', padding: '12px 16px', fontWeight: 700, fontSize: '0.9rem',
                background: isCurrentPlan ? '#059669' : colors.btn, color: '#fff', border: 'none',
                boxShadow: isCurrentPlan ? '0 4px 14px rgba(5,150,105,0.25)' : `0 4px 14px ${colors.accent}25`,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                cursor: isCurrentPlan ? 'default' : 'pointer', userSelect: 'none',
                opacity: checkoutLoading === plan.id ? 0.7 : 1,
                pointerEvents: checkoutLoading === plan.id ? 'none' as const : 'auto' as const,
              }}
              onMouseEnter={e => { if (!isCurrentPlan) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${colors.accent}35`; } }}
              onMouseLeave={e => { if (!isCurrentPlan) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 14px ${colors.accent}25`; } }}
            >
              {checkoutLoading === plan.id ? (
                <Spinner animation="border" size="sm" />
              ) : isCurrentPlan ? (
                <CheckIcon style={{ width: '16px', height: '16px' }} />
              ) : (
                <RocketLaunchIcon style={{ width: '16px', height: '16px' }} />
              )}
              {isCurrentPlan ? 'Current Plan' : (loggedInUser && !isFree(plan.price) ? 'Subscribe Now' : (plan.cta_text || 'Get Started'))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <PublicNavbar />

      {/* ─── Hero / Header ─── */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 40%, #3b82f6 100%)',
          paddingTop: '140px',
          paddingBottom: '4rem',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.05,
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center" style={{ marginBottom: '3rem' }}>
            <h1 style={{ fontWeight: 800, color: '#fff', fontSize: 'clamp(2rem, 4vw, 2.8rem)', marginBottom: '0.75rem', letterSpacing: '-0.03em' }}>
              Simple, transparent pricing
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto', lineHeight: 1.6 }}>
              Start free for 15 days. Upgrade anytime as your bakery grows.
            </p>
            <p style={{
              color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem',
              marginTop: '0.75rem', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              Built for micro bakeries managing wholesale orders
            </p>
          </div>

          {/* Plans */}
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            {checkoutError && (
              <Alert variant="danger" dismissible onClose={() => setCheckoutError('')} className="mb-4">
                {checkoutError}
              </Alert>
            )}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="light" />
              </div>
            ) : (
              <>
                {/* Free plan — full-width banner */}
                {freePlan && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    {renderPlanCard(freePlan, true)}
                  </div>
                )}

                {/* Paid plans — 3 columns */}
                <Row className="g-4 justify-content-center" style={{ alignItems: 'stretch' }}>
                  {paidPlans.map((plan) => (
                    <Col key={plan.id} xs={12} sm={6} lg={4} style={{ display: 'flex' }}>
                      {renderPlanCard(plan)}
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        </Container>
      </section>

      {/* ─── Trust Highlights ─── */}
      {highlights.length > 0 && (
        <section style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: '2.5rem 0' }}>
          <Container>
            <div className="d-flex flex-wrap justify-content-center gap-4">
              {highlights.map((text: string) => (
                <div key={text} className="d-flex align-items-center gap-2" style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500 }}>
                  <CheckIcon style={{ width: '18px', height: '18px', color: '#10b981', flexShrink: 0 }} />
                  {text}
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* ─── CTA Section ─── */}
      <section style={{ padding: '80px 0', background: '#0f172a', textAlign: 'center' }}>
        <Container>
          <h2 style={{
            fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
            fontWeight: 800,
            color: 'white',
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
            marginBottom: '1rem',
          }}>
            Ready to get started?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: '460px', margin: '0 auto 2rem' }}>
            Join bakeries that manage their entire wholesale operation with Sourdoughsy.
          </p>
          <Button
            size="lg"
            variant="link"
            onClick={navigateToRegister}
            className="d-inline-flex align-items-center gap-2"
            style={{
              borderRadius: '14px',
              padding: '14px 36px',
              fontWeight: 700,
              fontSize: '1rem',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              color: 'white',
              border: 'none',
              boxShadow: '0 8px 30px rgba(59,130,246,0.3)',
              textDecoration: 'none',
            }}
          >
            <RocketLaunchIcon style={{ width: '18px', height: '18px' }} />
            Start Your Free Trial
          </Button>
        </Container>
      </section>

      {/* ─── Footer ─── */}
      <PublicFooter />
    </div>
  );
};

export default Pricing;
